"use client";

import dynamic from "next/dynamic";
import React from "react";

const PracticeLeaderboard = dynamic(
  () => import("./PracticeLeaderboard").then((module) => module.PracticeLeaderboard),
  {
    loading: () => <PracticeLeaderboardSkeleton />,
    ssr: false,
  }
);

function PracticeLeaderboardSkeleton() {
  return (
    <section
      id="practice-leaderboard"
      className="border-t border-white/10 bg-[#111118] px-4 py-10 text-white sm:px-6 lg:py-12"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-3 h-5 w-44 animate-pulse rounded-full bg-orange-200/15" />
            <div className="mb-3 h-8 w-full max-w-xl animate-pulse rounded-lg bg-white/10" />
            <div className="h-4 w-full max-w-2xl animate-pulse rounded bg-white/[0.07]" />
            <div className="mt-2 h-4 w-4/5 max-w-xl animate-pulse rounded bg-white/[0.07]" />
          </div>
          <div className="h-11 w-full animate-pulse rounded-lg bg-white/10 md:w-80" />
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 shadow-2xl shadow-black/20 sm:p-5">
          <div className="space-y-3">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="h-14 animate-pulse rounded-lg bg-white/[0.06]" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function LazyPracticeLeaderboard() {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = React.useState(false);

  React.useEffect(() => {
    if (shouldLoad) return;

    const load = () => setShouldLoad(true);
    const container = containerRef.current;
    let idleId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let observer: IntersectionObserver | null = null;

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(load, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(load, 1800);
    }

    if (container && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            load();
          }
        },
        { rootMargin: "600px 0px" }
      );
      observer.observe(container);
    }

    return () => {
      if (idleId !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      observer?.disconnect();
    };
  }, [shouldLoad]);

  return (
    <div ref={containerRef}>
      {shouldLoad ? <PracticeLeaderboard /> : <PracticeLeaderboardSkeleton />}
    </div>
  );
}
